import { Injectable } from '@angular/core';
import { NFIdentity, ProcessTrigger, TransitionDisplayType } from '@nibras/core/common';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { AppCacheService } from './app-cache.service';
import { HttpService } from './http.service';
import { AttributeSmartValue } from './model';
import { attributeTemplateWithMapInfo } from './model/attribute-template-with-map-info.model';
import { dcAttributeTemplate } from './model/dcAttributeTemplate.model';
import { dcElement } from './model/dcElement.model';
import { dcElementTemplate } from './model/dcElementTemplate.model';
import { dcTag } from './model/dcTag.model';
import { dcAttributeHeader } from './model/dcattribute.model';
import { dcDataReference } from './model/dcdataReference.model';
import { dcElementHeader } from './model/dcelementheader.model';
import { dcQueryParameter } from './model/dcqueryParameter.model';
import { dcValue } from './model/dcvalue.model';
import { dcActionInfo } from './model/response/dcActionInfo.model';
import { dcSuggestedValue } from './model/suggested-value.model';
import { UserRoleRefIdDetails } from './model/user-role-ref-id-details.model';
import { ActionableActivityConfig } from './model/workflow/actionable-activity-config.model';
import { ActionableActivityConfigExtn } from './model/workflow/actionable-activity-configext.model';
import { ActionableProcessDefinition } from './model/workflow/actionable-process-definition.model';
import { ActionableProcessExecutionParams } from './model/workflow/actionable-process-execution-params.model';
import { ActionableProcessExecutionResult } from './model/workflow/actionable-process-execution-result.model';
import { ActionableProcessHeader } from './model/workflow/actionable-process-header.model';
import { ActionableTriggerConfig } from './model/workflow/actionable-trigger-config.model';
import { LinkedProcessInfo } from './model/workflow/linked-process-info.model';
import { dcProcessActivityFilter } from './model/workflow/process-activity-filter.model';
import { ProcessInstanceData } from './model/workflow/process-instance-data.model';
import { ProcessInstanceFilter } from './model/workflow/process-instance-filter.model';
import { ProcessInstanceRefElementMap } from './model/workflow/process-instance-refelementmap.model';
import { ProcessInstance } from './model/workflow/process-instance.model';
import { ProcessInstancesSearchResult } from './model/workflow/process-instances-search-result.model';
import { ProcessLinkDetail } from './model/workflow/process-link-detail.model';
import { ProcessProgress } from './model/workflow/processprogress.model';
import { RequestProcessInstanceInfo } from './model/workflow/request-process-instance-info';
import { SearchProcessInstanceParams } from './model/workflow/search-process-instance-params.model';
import { UserDetail } from './model/workflow/user-detail.model';

const CACHE_SIZE = 1;

@Injectable()
export class WorkFlowCommonService {


  private attributeTemplateWithMapInfoCache: Observable<attributeTemplateWithMapInfo>;

  constructor(private httpService: HttpService, private appCacheService: AppCacheService) { }

  public getParentActivityInstanceId(activities: ActionableActivityConfig[]) {
    let parentActivityInstanceId: string;
    if (activities && activities.length > 0) {
      parentActivityInstanceId = activities[0].ActivityID.toString();
    }
    return parentActivityInstanceId;
  }

  public fillUserNames(
    actionableProcessDefinition: ActionableProcessDefinition,
    RefElementVersionID: number
  ) {
    if (
      actionableProcessDefinition.ProcessDefinition &&
      actionableProcessDefinition.ProcessDefinition.ActionableActivities &&
      actionableProcessDefinition.ProcessDefinition.ActionableActivities.length > 0
    ) {
      actionableProcessDefinition.ProcessDefinition.ActionableActivities.forEach(
        actionableActivities => {
          if (
            actionableActivities.ExecutorRoleID &&
            actionableProcessDefinition &&
            actionableProcessDefinition.RefRootElementContext &&
            actionableProcessDefinition.RefRootElementContext.length > 0
          ) {
            actionableProcessDefinition.RefRootElementContext.forEach(
              actionableProcessRefElementContext => {
                if (
                  actionableProcessRefElementContext.RefRootElementVersionID ==
                  RefElementVersionID &&
                  actionableProcessRefElementContext.ExecutorDetail &&
                  actionableProcessRefElementContext.ExecutorDetail.length
                ) {
                  const securityUserRoleMap =
                    actionableProcessRefElementContext.ExecutorDetail.find(
                      a => a.ExecutorRoleID == actionableActivities.ExecutorRoleID
                    );
                  if (
                    securityUserRoleMap &&
                    securityUserRoleMap.SecurityUserDetail &&
                    securityUserRoleMap.SecurityUserDetail.length > 0
                  ) {
                    actionableActivities.UserDetails = [];
                    securityUserRoleMap.SecurityUserDetail.forEach(securityUserDetail => {
                      if (securityUserDetail.Users && securityUserDetail.Users.length > 0) {
                        securityUserDetail.Users.forEach(userDetail => {
                          actionableActivities.UserDetails.push(userDetail);
                        });
                      }
                    });
                  }
                }
              }
            );
          } else {
            if (actionableActivities.ExecutorRoleName) {
              const userDetail = { UserName: actionableActivities.ExecutorRoleName } as UserDetail;
              actionableActivities.UserDetails = [userDetail];
              actionableActivities.ExecutorRoleName = null;
            }
          }
        }
      );
    }
  }

  public filterTriggers(
    selectecActionableActivity: ActionableActivityConfigExtn,
    processInstance: ProcessInstanceData
  ) {
    let filterTriggers: ActionableTriggerConfig[] = [];
    if (
      selectecActionableActivity &&
      selectecActionableActivity.Triggers &&
      selectecActionableActivity.Triggers.length > 0
    ) {
      filterTriggers = selectecActionableActivity.Triggers.filter(
        a =>
          a.TriggerDisplayType == TransitionDisplayType.Always ||
          a.TriggerDisplayType == TransitionDisplayType.Once
      );
      if (processInstance) {
        const repeatedActivities = processInstance.ActivityInstances.filter(
          a =>
            a.ActivityID == processInstance.ProcessDefinition.CurrentActivityDefinition.ActivityID
        );
        if (repeatedActivities && repeatedActivities.length > 1) {
          filterTriggers = selectecActionableActivity.Triggers.filter(
            a =>
              a.TriggerDisplayType == TransitionDisplayType.Always ||
              a.TriggerDisplayType == TransitionDisplayType.OnRepeat
          );
        }
      }
    }
    selectecActionableActivity.Triggers = filterTriggers;
  }

  public checkProcessingAccess(processInstanceData: ProcessInstanceData) {
    if (processInstanceData && processInstanceData.activeActivityInstance) {
      return processInstanceData.activeActivityInstance.CurrentUserIsExecutor;
    } else if (
      processInstanceData &&
      processInstanceData.ActivityInstances &&
      processInstanceData.ActivityInstances.length > 0
    ) {
      const activeActivityInstance = processInstanceData.ActivityInstances.find(
        a => a.EndDate == null
      );
      if (activeActivityInstance && activeActivityInstance.CurrentUserIsExecutor) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  public fillCompletedActivityUserDetails(processInstanceData: ProcessInstanceData) {
    const selectedActivityIndex =
      processInstanceData.ProcessDefinition.ProcessDefinition.ActionableActivities.findIndex(
        a =>
          a.ActivityID == processInstanceData.ProcessDefinition.CurrentActivityDefinition.ActivityID
      );

    processInstanceData.ProcessDefinition.ProcessDefinition.ActionableActivities.forEach(
      (activity, i) => {
        activity.disableExpand = false;
        activity.EnableViewMode = true;
        if (i <= selectedActivityIndex) {
          activity.IsCompleted = true;
          if (selectedActivityIndex == i) {
            activity.IsSelected = true;
          }
          if (!activity.IsSelected) {
            this.setExecutorDetailsFromInstance(activity, processInstanceData);
          } else if (
            activity.ActivityName ==
            processInstanceData.ProcessDefinition.CurrentActivityDefinition.ActivityName
          ) {
            this.setExecutorDetailsFromInstance(activity, processInstanceData);
          }
        }
      }
    );
  }

  private setExecutorDetailsFromInstance(
    activity: ActionableActivityConfig,
    processInstanceData: ProcessInstanceData
  ) {
    const filterActivityInstance = processInstanceData.ActivityInstances.filter(
      a => a.ActivityID == activity.ActivityID
    );
    if (filterActivityInstance && filterActivityInstance.length > 0) {
      activity.ExecutorRoleName = undefined;
      activity.Remarks = filterActivityInstance.map(activityInstance => activityInstance.Remark);

      if (filterActivityInstance[filterActivityInstance.length - 1].ExecutorRoleName) {
        activity.ExecutorRoleName =
          filterActivityInstance[filterActivityInstance.length - 1].ExecutorRoleName;
      }

      if (filterActivityInstance[filterActivityInstance.length - 1].ExecutorUserID) {
        activity.UserDetails = [
          {
            UserID: filterActivityInstance[filterActivityInstance.length - 1].ExecutorUserID,
            UserName: filterActivityInstance[filterActivityInstance.length - 1].ExecutorUserName
          } as UserDetail
        ];
      }
    }
  }

  public setCurrentActivityInstance(processElement: ProcessInstanceData) {
    if (processElement.ActivityInstances) {
      const currentActivity = processElement.ActivityInstances.find(a => a.EndDate == null);

      if (currentActivity) {
        processElement.activeActivityInstance = currentActivity;
      }
    }
  }

  public setProcessElement(sourceProcessInstance: ProcessInstanceData) {
    this.setFilterWidgets(sourceProcessInstance);
    return sourceProcessInstance;
  }

  private setFilterWidgets(processElement: ProcessInstanceData) {
    if (processElement?.ActivityInstances && processElement?.ActivityInstances?.length > 0) {
      if (processElement?.ProcessDefinition?.CurrentActivityDefinition) {
        if (
          processElement.ProcessDefinition.CurrentActivityDefinition.Tags &&
          processElement.ProcessDefinition.CurrentActivityDefinition.Tags.length > 0
        ) {
          const widgetIDs: number[] =
            processElement.ProcessDefinition.CurrentActivityDefinition?.Tags.map(widgetTag => {
              if (widgetTag.ElementTypeID == NFIdentity.Widget) {
                return widgetTag.SourceObjectID;
              }
            }).filter(widget => widget);
          if (widgetIDs && widgetIDs.length > 0) {
            processElement.WidgetIds = widgetIDs;
          }
        }
        processElement.DefaultWidgetName =
          processElement.ProcessDefinition.CurrentActivityDefinition.DefaultWidgetDescription;
        processElement.ActivityName =
          processElement.ProcessDefinition.CurrentActivityDefinition.ActivityName;
      }
    }
  }

  public createInstanceRole(
    ProcessName: string,
    dctag: dcTag,
    userID: number,
    CheckIn: boolean,
    processTags: dcTag[],
    Comments: string,
    IsGuest: boolean
  ): Observable<any> {
    const req = this.httpService.post<any>('Process/createInstanceRole', {
      processName: ProcessName,
      dctag,
      CreatedBy: userID,
      CheckIn,
      processTags,
      Comment: Comments,
      IsGuest
    });
    return req;
  }

  public GetTaskElements(selectedTile: string): Observable<ProcessInstance[]> {
    return this.httpService.get(`Process/GetTaskElements?selectedTile=${selectedTile}`);
  }

  public getProcessElementBulk(
    requestProcessInstanceInfo: RequestProcessInstanceInfo
  ): Observable<ProcessInstance[]> {
    return this.httpService.post<ProcessInstance[]>(
      'Process/GetProcessElementByIDBulk',
      requestProcessInstanceInfo
    );
  }

  public filterProcessDesignElements(
    processInstance: ProcessInstanceData,
    processFilterTagName: string,
    referenceElementTemlateNames: string[],
    processList: ActionableProcessHeader[]
  ) {
    let filterData: ActionableProcessHeader[];
    if (processFilterTagName || (processInstance && referenceElementTemlateNames?.length)) {
      const _processFilterTagNames =
        (processFilterTagName && processFilterTagName.trim().split(',')) || [];
      filterData = processList.filter(element => {
        const tags =
          (element &&
            element.Tags &&
            element.Tags.filter(
              a =>
                a.SourceClassID === NFIdentity.ElementTemplate ||
                a.ElementTypeID == NFIdentity.Process_Category
            )) ||
          [];
        let isFilterTag = false;
        let isSupportedTemplates = false;
        let isCategoryTag = false;
        let supportedTriggerSource = false;
        let hasTriggerSourceTag = false;
        tags.forEach(tag => {
          if (
            !isFilterTag &&
            _processFilterTagNames?.length &&
            tag.ElementTypeID === NFIdentity.Process_Category
          ) {
            isCategoryTag = true;
            isFilterTag = _processFilterTagNames.includes(tag.TagName);
          }

          if (
            !isSupportedTemplates &&
            processInstance &&
            referenceElementTemlateNames.length &&
            tag.SourceClassID === NFIdentity.ElementTemplate &&
            tag.TagName !== 'BT_BASE_ELEMENT'
          ) {
            isSupportedTemplates = referenceElementTemlateNames.includes(tag.TagName);
          }
          // else {
          //   isSupportedTemplates = true;
          // }

          if (!supportedTriggerSource && tag.ElementTypeID === NFIdentity.TriggerSource) {
            hasTriggerSourceTag = true;
            supportedTriggerSource = ProcessTrigger[processInstance.TriggerSource] === tag.TagName;
          }
        });
        const _triggerSourceSupport =
          (hasTriggerSourceTag && supportedTriggerSource) || !hasTriggerSourceTag ? true : false;
        if (isCategoryTag && isFilterTag && isSupportedTemplates && _triggerSourceSupport)
          return true;
        else if (!isCategoryTag && isSupportedTemplates && _triggerSourceSupport) return true;
        else return false;
      });
    } else {
      filterData = processList.filter(element => {
        if (element?.Tags?.length) {
          return !element.Tags.some(tag => tag.ElementTypeID === NFIdentity.TriggerSource);
        } else return true;
      });
    }

    return filterData;
  }

  public getActiveProcessInstances(
    processElementVersionID: number,
    refRootElementVersionID: number[]
  ) {
    return this.httpService.post<ProcessInstanceRefElementMap[]>(
      `Process/GetActiveProcessInstances/${processElementVersionID}`,
      refRootElementVersionID,
      'GetActiveProcessInstances' + processElementVersionID + refRootElementVersionID.join(',')
    );
  }

  //New Implementation
  //#1 call to fetch all assigned processes for the role
  public getActionableProcessHeaderByRole() {
    return this.httpService.get<ActionableProcessHeader[]>(
      `Process/GetActionableProcessHeaderByRole`,
      null,
      'GetActionableProcessHeaderByRole'
    );
  }

  //#2 call to fetch process configuration, read write user assignment for the selected reference element and current activity
  public getActionableProcessDefinition(
    processElementVersionID: number,
    refRootElementVersionID: number[],
    parentProcessInstanceID = 0
  ) {
    return this.httpService.post<ActionableProcessDefinition>(
      `Process/GetActionableProcessDefinition/${processElementVersionID}/${parentProcessInstanceID}`,
      refRootElementVersionID,
      'GetActionableProcessDefinition' + processElementVersionID + refRootElementVersionID.join(',')
    );
  }

  //#3 call to create new or moving form one activity to another
  public executeProcessInstance(processEngineParams: ActionableProcessExecutionParams[]) {
    const req = this.httpService.post<ActionableProcessExecutionResult[]>(
      'Process/ExecuteProcessInstance',
      processEngineParams
    );
    return req;
  }

  //#4 call to get process instance
  public getProcessInstance(processInstanceFilter: ProcessInstanceFilter) {
    const req = this.httpService.post<ProcessInstanceData>(
      'Process/GetProcessInstance',
      processInstanceFilter
    );
    return req;
  }

  public searchUserRefIdDetail(
    searchText: string,
    filterType: number
  ): Observable<UserRoleRefIdDetails[]> {
    // const req = this.httpService.post<UserRoleSerch[]>('Process/GetUserRefIdDetails', {
    //   searchText: searchText,
    //   filterType: filterType
    // });
    return this.httpService.get<UserRoleRefIdDetails[]>(
      `Process/GetUserRefIdDetails?searchText=${searchText}&filterType=${filterType}`
    );
    //  return req;
  }

  public getElementWithAttributeValues(elementVerionID: number): Observable<dcElement> {
    return this.httpService.get(
      `Process/GetElementWithAttributeValues?processElementVersionId=${elementVerionID}`
    );
  }

  public saveAnalysisElements(
    queryName: string,
    analysisElemets: dcElement[],
    dataReference: dcDataReference,
    analysisAttributeTemplate: dcAttributeTemplate,
    selectedTemplateId: number,
    isFormBuilderFlag = false,
    isUpdateFlag: boolean,
    analysisTemplateID: number,
    isElementFormulaUpdate = false,
    elementAttributeMapId = 0
  ): Observable<dcActionInfo<any>> {
    return this.httpService.post<dcActionInfo<any>>(`Process/InsertAnalysisElementData`, {
      QueryName: queryName,
      AnalysisElements: analysisElemets,
      FormulaDataReference: dataReference,
      AnalysisAttributeTemplate: analysisAttributeTemplate,
      RootElementTemplateVersionId: selectedTemplateId,
      isFormBuilderFlag,
      isUpdateFlag,
      AnalysisTemplateID: analysisTemplateID,
      IsElementFormulaUpdate: isElementFormulaUpdate,
      ElementAttributeMapId: elementAttributeMapId
    });
  }

  public updateAnalysisAttribute(analysisElementTemplate: dcElementTemplate[]): Observable<string> {
    return this.httpService.post<string>(`Process/UpdateAnalysisAttribute`, {
      AnalysisElementTemplate: analysisElementTemplate
    });
  }

  public getProcessPersistentByID(
    processElementVersionId: number
  ): Observable<dcAttributeHeader[]> {
    return this.httpService.get<dcAttributeHeader[]>(
      'Process/GetProcessPersistenByID?processInstanceID=' + processElementVersionId
    );
  }

  public getProcessDetailsForAnalysisAttribute(
    attributeTemplateVersionId: number
  ): Observable<any> {
    const req = this.httpService.post<any>(`Process/GetProcessDetailsForAnalysisAttribute`, {
      AttributeTemplateVersionId: attributeTemplateVersionId
    });
    return req;
  }

  public getProcessElementTemplateByID(
    elementVersionID: number,
    rootElementVersionID = 0
  ): Observable<dcElement[]> {
    return this.httpService.get(
      `Process/GetProcessElementTemplateByID?elementVersionID=${elementVersionID}&triggerActionElementVersionID=${rootElementVersionID}`
    );
  }

  public getProcessElement(
    processElementVersionId: number,
    isProcessExecutedByRole = 0,
    hierarchyCategoryID = 0
  ): Observable<ProcessInstance> {
    return this.httpService.get(
      // eslint-disable-next-line max-len
      `Process/GetProcessElementByID?processElementVersionId=${processElementVersionId}&IsProcessExecutedByRole=${isProcessExecutedByRole}&roleHierarchyCategoryID=${hierarchyCategoryID}`
    );
  }

  public getChildProcessElement(processElementVersionId: number): Observable<dcElement[]> {
    return this.httpService.get(
      `Process/GetProcessChildElementByID?processElementVersionId=${processElementVersionId}`
    );
  }

  public getProcessActivities(
    activityFilter: dcProcessActivityFilter,
    cancelToken?: string
  ): Observable<ProcessInstance[]> {
    return this.httpService.post('Process/GetProcessActivities', activityFilter, cancelToken);
  }

  public getProcessTriggerByID(processElementVersionId: number) {
    return this.httpService.get<dcQueryParameter[]>(
      `Process/GetProcessTriggerByID?processElementVersionId=${processElementVersionId}`
    );
  }

  public getSupportedElementHeader(supportedTemplateIds: number[]): Observable<dcElementHeader[]> {
    const req = this.httpService.post<dcElementHeader[]>(
      'Process/GetSupportedElementHeaders',
      supportedTemplateIds
    );
    return req;
  }

  public getElementByIdList(supportedElementIds: number[]): Observable<dcElementHeader[]> {
    const req = this.httpService.post<dcElementHeader[]>(
      'Process/GetElementByIdList',
      supportedElementIds
    );
    return req;
  }

  public getAvailableStateByID(elementVersionID: number): Observable<Array<ProcessProgress>> {
    return this.httpService.get(
      `Process/GetAvailableStateByID?elementVersionID=${elementVersionID}`
    );
  }

  public getFaultTreeTagsByTemplateTag(elementTag: dcTag): Observable<dcTag[]> {
    const req = this.httpService.post<dcTag[]>(`Process/GetFaultTreeTagsByTemplateTag`, elementTag);
    return req;
  }

  public getFaultTreeTagsByElementTag(elementTag: dcTag): Observable<dcTag[]> {
    const req = this.httpService.post<dcTag[]>(`Process/GetFaultTreeTagsByElementTag`, elementTag);
    return req;
  }

  public createInstance(
    ProcessName: string,
    dctag: dcTag,
    userID: number,
    CheckIn = true,
    ParentProcessInstanceID?: number,
    ParentActivityInstanceID?: number,
    RootCauseElementVersionID?: number
  ): Observable<ProcessInstance> {
    const req = this.httpService
      .post<ProcessInstance>('Process/CreateInstance', {
        processName: ProcessName,
        dctag,
        CreatedBy: userID,
        CheckIn,
        ParentProcessInstanceID,
        ParentActivityInstanceID,
        RootCauseElementVersionID
      })
      .pipe(tap(() => (this.appCacheService.refreshTaskViewer = true)));

    return req;
  }

  public createInstanceBulk(
    ProcessName: string,
    dctags: dcTag[],
    userID: number,
    CheckIn = true,
    ParentProcessInstanceID?: number,
    ParentActivityInstanceID?: number,
    RootCauseElementVersionID?: number
  ): Observable<dcActionInfo<ProcessInstance>> {
    const req = this.httpService
      .post<dcActionInfo<ProcessInstance>>('Process/CreateInstanceBulk', {
        processName: ProcessName,
        dctags,
        CreatedBy: userID,
        CheckIn,
        ParentProcessInstanceID,
        ParentActivityInstanceID,
        RootCauseElementVersionID
      })
      .pipe(tap(() => (this.appCacheService.refreshTaskViewer = true)));
    return req;
  }

  public getElementListForFormula(queryId: number): Observable<attributeTemplateWithMapInfo> {
    if (!this.attributeTemplateWithMapInfoCache) {
      this.attributeTemplateWithMapInfoCache = this.getElementListForFormula2(queryId).pipe(
        shareReplay(CACHE_SIZE)
      );
    }

    return this.attributeTemplateWithMapInfoCache;
  }

  public getElement(elementVersionId: number) {
    return this.httpService.get<dcElementHeader[]>(
      `Element/GetElementByID?elementVersionId=${elementVersionId}`
    );
  }

  public getElementListForFormula2(queryId: number): Observable<attributeTemplateWithMapInfo> {
    return this.httpService
      .post<attributeTemplateWithMapInfo>(`Element/GetElementListByQueryId/${queryId}`, null)
      .pipe(map(res => res));
  }

  public getSuggestedValues(): Observable<dcSuggestedValue[]> {
    return this.httpService.get(`Element/GetSuggestedValues`);
  }

  public getFormulaSimulationResult(
    formulaExpression: string,
    simulationObject: any,
    valueTypeIDResult = 0
  ): Observable<dcValue> {
    return this.httpService.post<dcValue>(`Element/GetFormulaSimulationResult`, {
      FormulaExpression: formulaExpression,
      SimulationObject: simulationObject,
      ValueTypeIDResult: valueTypeIDResult
    });
  }

  //#region Manage Process Linking
  /**
   * Fetches the linked processes based on the provided process instance ID.
   * @param processInstanceId - The ID of the process instance to get linked processes for.
   * @returns An observable stream containing the linked processes information.
   */
  public getLinkedProcesses(processInstanceId: number): Observable<LinkedProcessInfo[]> {
    return this.httpService.get<LinkedProcessInfo[]>(
      `Process/GetLinkedProcesses?processInstanceId=${processInstanceId}`
    );
  }

  /**
   * Manages the process link by sending the details of the process link.
   * @param processLinkDetail - The details of the process link to manage.
   * @returns An observable stream containing the result as an array of strings.
   */
  public manageProcessLink(processLinkDetail: ProcessLinkDetail): Observable<string[]> {
    return this.httpService.post<string[]>(`Process/ManageProcessLink`, processLinkDetail);
  }

  /**
   * Searches for process instances based on the provided search parameters.
   * @param searchProcessInstanceParams - The search parameters used to filter process instances.
   * @returns An observable stream containing the search results as an array of ProcessInstancesSearchResult.
   */
  public searchProcessInstances(
    searchProcessInstanceParams: SearchProcessInstanceParams
  ): Observable<ProcessInstancesSearchResult[]> {
    return this.httpService.post<ProcessInstancesSearchResult[]>(
      `Process/SearchProcessInstances`,
      searchProcessInstanceParams
    );
  }
  //#endregion

  //#region Auto Save Persistent Data
  /**
   * Executes a self-transition for the given process instance.
   * Returns a Promise that resolves when execution completes.
   */
  public executeSelfTransition(
    processElement: ProcessInstanceData,
    attributeSmartValues: AttributeSmartValue[]
  ): Observable<dcActionInfo<string>[]> {
    // Prepare execution parameters array
    const actionableProcessExecutionParams: ActionableProcessExecutionParams[] = [
      {
        ProcessInstanceID: processElement.ProcessInstanceID,
        CurrentActivityInstanceID: processElement.activeActivityInstance.ActivityInstanceID,
        ActivityID: processElement.ProcessDefinition.CurrentActivityDefinition.ActivityID,
        ActivityPersistentData: attributeSmartValues,
        IsWriteToPersistent: processElement.currentCommand.IsWriteToPersistent,
        IsWriteToSource: processElement.currentCommand.IsWriteToSource,
      } as ActionableProcessExecutionParams
    ];

    return this.httpService.post<dcActionInfo<string>[]>(
      'Process/ExecuteSelfTransitionAutoSave',
      actionableProcessExecutionParams
    );
  }
  //#endregion
}
